import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { DemoLoginButton } from "@/components/DemoLoginButton";
import { useI18n } from "@/lib/i18n";

function makeSignUpSchema(t: (k: string) => string) {
  return z.object({
    displayName: z.string().trim().min(1, t("auth.zod.nameRequired")).max(100, t("auth.zod.nameTooLong")),
    email: z.string().trim().email(t("auth.zod.emailInvalid")).max(255, t("auth.zod.emailTooLong")),
    password: z
      .string()
      .min(8, t("auth.zod.passwordTooShort"))
      .max(72, t("auth.zod.passwordTooLong"))
      .regex(/[A-Z]/, t("auth.zod.passwordUppercase"))
      .regex(/[a-z]/, t("auth.zod.passwordLowercase"))
      .regex(/[0-9]/, t("auth.zod.passwordNumber")),
  });
}

function makeSignInSchema(t: (k: string) => string) {
  return z.object({
    email: z.string().trim().email(t("auth.zod.emailInvalid")).max(255),
    password: z.string().min(1, t("auth.zod.passwordRequired")).max(72),
  });
}

export const Route = createFileRoute("/auth")({
  component: AuthPage,
  head: () => ({
    meta: [
      { title: "Sign in or create an account · AdKavi" },
      { name: "description", content: "Log in or create your AdKavi account to start scaling ads with AI." },
    ],
  }),
});

function AuthPage() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [loading, setLoading] = useState(false);
  const [returningUser, setReturningUser] = useState(false);
  const [showSignInPassword, setShowSignInPassword] = useState(false);
  const [showSignUpPassword, setShowSignUpPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [confirmError, setConfirmError] = useState("");

  async function checkReturningUser(value: string) {
    const trimmed = value.trim();
    if (!trimmed || !z.string().email().safeParse(trimmed).success) {
      setReturningUser(false);
      return;
    }
    const { data, error } = await supabase.rpc("email_trial_used", { _email: trimmed });
    if (error) {
      console.warn("email_trial_used check failed", error);
      return;
    }
    setReturningUser(Boolean(data));
  }

  useEffect(() => {
    const handleSession = (session: any) => {
      if (!session) return;
      let pendingPlan: string | null = null;
      try {
        pendingPlan = sessionStorage.getItem("adkavi:pendingPlan");
        if (pendingPlan) sessionStorage.removeItem("adkavi:pendingPlan");
      } catch {
        // ignore
      }
      if (pendingPlan) {
        navigate({ to: "/checkout/$priceId", params: { priceId: pendingPlan } });
      } else {
        navigate({ to: "/dashboard" });
      }
    };
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      handleSession(session);
    });
    supabase.auth.getSession().then(({ data: { session } }) => handleSession(session));
    return () => subscription.unsubscribe();
  }, [navigate]);

  async function handleSignUp(e: React.FormEvent) {
    e.preventDefault();
    if (password !== confirmPassword) {
      setConfirmError(t("auth.passwordMismatch"));
      return;
    }
    setConfirmError("");
    const parsed = makeSignUpSchema(t).safeParse({ displayName, email, password });
    if (!parsed.success) {
      toast.error(parsed.error.issues[0].message);
      return;
    }
    setLoading(true);
    const { error } = await supabase.auth.signUp({
      email: parsed.data.email,
      password: parsed.data.password,
      options: {
        emailRedirectTo: `${window.location.origin}/dashboard`,
        data: { display_name: parsed.data.displayName },
      },
    });
    setLoading(false);
    if (error) {
      toast.error(error.message.includes("already") ? t("auth.toast.exists") : error.message);
      return;
    }
    toast.success(t("auth.toast.checkInbox"));
    navigate({ to: "/verify-email", search: { email: parsed.data.email } });
  }

  async function handleSignIn(e: React.FormEvent) {
    e.preventDefault();
    console.log("[auth] handleSignIn triggered");
    const parsed = makeSignInSchema(t).safeParse({ email, password });
    if (!parsed.success) {
      console.log("[auth] validation failed:", parsed.error.issues[0].message);
      toast.error(parsed.error.issues[0].message);
      return;
    }
    setLoading(true);
    console.log("[auth] calling signInWithPassword for", parsed.data.email);
    const { data, error } = await supabase.auth.signInWithPassword(parsed.data);
    console.log("[auth] signInWithPassword result:", { user: data?.user?.id, email_confirmed_at: data?.user?.email_confirmed_at, error });
    setLoading(false);
    if (error) {
      const msg = error.message.toLowerCase();
      console.log("[auth] sign-in error:", error.message);
      if (msg.includes("not confirmed") || msg.includes("confirm")) {
        toast.error(t("auth.toast.verifyFirst"));
        navigate({ to: "/verify-email", search: { email: parsed.data.email } });
        return;
      }
      toast.error(msg.includes("invalid") ? t("auth.toast.invalidCreds") : error.message);
      return;
    }
    console.log("[auth] sign-in success, navigating to /dashboard");
    toast.success(t("auth.welcomeBack"));
    navigate({ to: "/dashboard" });
  }

  return (
    <>
      <Navbar />
      <main className="min-h-screen flex items-center justify-center px-6 bg-background">
        <div className="w-full max-w-md">


        <div className="rounded-2xl border border-border/60 bg-card p-8">
          
          <Button
            type="button"
            variant="outline"
            className="w-full mb-4"
            onClick={async () => {
              const result = await lovable.auth.signInWithOAuth("google", {
                redirect_uri: window.location.origin,
              });
              if (result.error) toast.error(result.error.message || t("auth.googleFailed"));
            }}
          >
            <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.25 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l3.66-2.84z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
            </svg>
            {t("auth.continueGoogle")}
          </Button>
          <div className="relative mb-4">
            <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-border/60" /></div>
            <div className="relative flex justify-center text-xs"><span className="bg-card px-2 text-muted-foreground">{t("auth.or")}</span></div>
          </div>
          <Tabs defaultValue="signin" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="signin">{t("auth.tab.signin")}</TabsTrigger>
              <TabsTrigger value="signup">{t("auth.tab.signup")}</TabsTrigger>
            </TabsList>

            <TabsContent value="signin">
              <form onSubmit={handleSignIn} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="signin-email">{t("auth.email")}</Label>
                  <Input id="signin-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="signin-password">{t("auth.password")}</Label>
                    <Link to="/forgot-password" className="text-xs text-primary hover:underline">
                      {t("auth.forgot")}
                    </Link>
                  </div>
                  <div className="relative">
                    <Input
                      id="signin-password"
                      type={showSignInPassword ? "text" : "password"}
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowSignInPassword((s) => !s)}
                      aria-label={showSignInPassword ? t("auth.hidePassword") : t("auth.showPassword")}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showSignInPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <Button type="submit" disabled={loading} className="w-full bg-orange-500 hover:bg-orange-600 text-white">
                  {loading ? t("auth.signingIn") : t("auth.signIn")}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="signup">
              <form onSubmit={handleSignUp} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="signup-name">{t("auth.name")}</Label>
                  <Input id="signup-name" type="text" required value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-email">{t("auth.email")}</Label>
                  <Input
                    id="signup-email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onBlur={(e) => checkReturningUser(e.target.value)}
                  />
                </div>
                {returningUser && (
                  <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 text-sm">
                    <p className="font-medium">{t("auth.returning.title")}</p>
                    <p className="text-muted-foreground mt-0.5">
                      {t("auth.returning.desc")}
                    </p>
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="signup-password">{t("auth.password")}</Label>
                  <div className="relative">
                    <Input
                      id="signup-password"
                      type={showSignUpPassword ? "text" : "password"}
                      required
                      minLength={8}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowSignUpPassword((s) => !s)}
                      aria-label={showSignUpPassword ? t("auth.hidePassword") : t("auth.showPassword")}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showSignUpPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-muted-foreground">{t("auth.passwordHint")}</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-confirm">{t("auth.confirmPassword")}</Label>
                  <div className="relative">
                    <Input
                      id="signup-confirm"
                      type={showConfirmPassword ? "text" : "password"}
                      required
                      minLength={8}
                      value={confirmPassword}
                      onChange={(e) => {
                        setConfirmPassword(e.target.value);
                        if (confirmError) setConfirmError("");
                      }}
                      className="pr-10"
                      aria-invalid={!!confirmError}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword((s) => !s)}
                      aria-label={showConfirmPassword ? t("auth.hidePassword") : t("auth.showPassword")}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {confirmError && (
                    <p className="text-xs text-destructive">{confirmError}</p>
                  )}
                </div>
                <Button type="submit" disabled={loading} className="w-full bg-orange-500 hover:bg-orange-600 text-white">
                  {loading ? t("auth.creating") : returningUser ? t("auth.signupChoosePlan") : t("auth.startTrial")}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </div>
        <div className="mt-4">
          <DemoLoginButton variant="steel" />
          <p className="mt-2 text-center text-xs text-muted-foreground">
            {t("auth.demoHint")}
          </p>
        </div>
        </div>
      </main>
    </>
  );
}
